import { useEffect, useRef } from 'react';
import { diffPlanEntitlements } from '../../services/planCatalog.js';

/**
 * Confirmação de troca de plano (P23C): mostra o que se ganha (upgrade) ou
 * se perde (downgrade) antes de iniciar o checkout — "Explique mudanças
 * antes da confirmação" / "Upgrade informa ganho funcional" / "Downgrade
 * informa perdas".
 *
 * Props:
 *   fromPlan   objeto do catálogo (GET /billing/plans) — plano atual
 *   toPlan     objeto do catálogo — plano de destino
 *   isDowngrade boolean
 *   loading    boolean — desabilita os botões enquanto o checkout é criado
 *   onConfirm  () => void
 *   onClose    () => void
 */
export function PlanChangeDialog({ fromPlan, toPlan, isDowngrade, loading, onConfirm, onClose }) {
  const overlayRef = useRef(null);
  const { gains, losses } = diffPlanEntitlements(fromPlan, toPlan);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={isDowngrade ? 'Confirmar downgrade de plano' : 'Confirmar upgrade de plano'}
    >
      <div className="bg-surface-container-low rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col shadow-xl overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-outline-variant shrink-0">
          <h2 className="text-lg font-bold">
            {isDowngrade ? `Fazer downgrade para ${toPlan?.name}` : `Fazer upgrade para ${toPlan?.name}`}
          </h2>
          <button onClick={onClose} aria-label="Fechar" className="text-on-surface-variant hover:text-on-surface transition-colors text-2xl leading-none">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
          {gains.length > 0 && (
            <div>
              <p className="font-semibold text-on-surface mb-2">Você ganha:</p>
              <ul className="space-y-1.5">
                {gains.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-on-surface-variant">
                    <span className="material-symbols-outlined text-primary text-base shrink-0">add_circle</span>
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {losses.length > 0 && (
            <div>
              <p className="font-semibold text-on-surface mb-2">Você perde:</p>
              <ul className="space-y-1.5">
                {losses.map((l, i) => (
                  <li key={i} className="flex items-start gap-2 text-on-surface-variant">
                    <span className="material-symbols-outlined text-error text-base shrink-0">remove_circle</span>
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isDowngrade && (
            <p className="text-xs text-on-surface-variant/70 border-t border-outline-variant/20 pt-3">
              Suas anotações, planos de leitura e trilhas já criados continuam acessíveis, legíveis e exportáveis —
              o limite novo vale só para itens novos a partir de agora.
            </p>
          )}

          <p className="text-xs text-on-surface-variant/70">
            A troca é aplicada imediatamente após a confirmação do pagamento.
          </p>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-outline-variant shrink-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 font-semibold py-2.5 rounded-xl text-sm bg-surface-container-high hover:bg-surface-container text-on-surface transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 font-semibold py-2.5 rounded-xl text-sm bg-primary text-on-primary hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? 'Aguarde...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
